import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/components/ui/cn";

const vButtonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg font-medium transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
    "disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary-600 text-on-primary shadow-xs hover:bg-primary-700 active:bg-primary-800",
        outline:
          "border border-border bg-card text-foreground shadow-xs hover:border-neutral-300 hover:bg-muted/60",
        ghost: "text-foreground hover:bg-muted",
        destructive:
          "bg-error-600 text-white shadow-xs hover:bg-error-700 active:bg-error-800",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-6 text-base",
        // Icoonknoppen: "icon" hoort bij md-knoppen (h-10), "iconSm" bij
        // sm-knoppen (h-8). Gebruik binnen één actiegroep altijd één formaat.
        icon: "h-10 w-10",
        iconSm: "h-8 w-8",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  },
);

type VButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof vButtonVariants> & {
    icon?: ReactNode;
    asChild?: boolean;
  };

export const VButton = forwardRef<HTMLButtonElement, VButtonProps>(
  (
    { className, variant, size, fullWidth, icon, asChild = false, children, ...props },
    ref,
  ) => {
    const classes = cn(vButtonVariants({ variant, size, fullWidth }), className);
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button ref={ref} className={classes} {...props}>
        {icon}
        {children}
      </button>
    );
  },
);
VButton.displayName = "VButton";

export type { VButtonProps };
